/**
 * ClientFlow Dashboard / Oversikt — premium polish pass.
 *
 * Layout/visual rev tuned against the May 2026 reference image.
 * Functionality, plan-gates, role permissions and backend contracts
 * are all unchanged from the previous Phase B+C revision.
 *
 * Key visual changes vs previous rev:
 *   * Page-internal topbar (Ny booking + Support + Profile menu).
 *   * Employee filter rendered as a glassy card with avatar stack
 *     (not a bare select trigger).
 *   * KPI cards have an emerald glow ring + soft outer shadow for
 *     premium depth.
 *   * Main layout: Calendar widget owns the full right column, runs
 *     tall from the KPI row down through the content. Dagens status
 *     sits directly below.
 *   * Kommende avtaler / Kunder å følge opp / Siste aktivitet rows
 *     get larger avatars, emerald initial chips, and bolder typography.
 *   * Activity feed shows a delivered-checkmark for sent emails and
 *     a relative timestamp on the right (matches reference).
 */

import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatNOK } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatGridSkeleton, HeaderSkeleton } from "@/components/Skeletons";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  TrendingUp, CalendarDays, UserCheck, AlertTriangle,
  Sparkles, ArrowRight, ArrowUpRight, ArrowDownRight, Clock,
  CheckCircle2, MessageCircle, UserPlus, FileText, Mail, Coffee,
  CalendarPlus, CalendarCheck, Users, Activity, Zap, Bell, X, ChevronDown,
  BellRing, Heart, LifeBuoy, Settings, LogOut, Plus, Check, Megaphone, BarChart3,
  Scissors, UserCog, Link2, Play, Square, Hourglass,
} from "lucide-react";
import { useContact } from "@/components/ContactModal";
import { useAuth } from "@/context/AuthContext";
import FollowUpList from "@/components/FollowUpList";
import PricingInterestCard from "@/components/PricingInterestCard";
import { toast } from "sonner";

// =====================================================================
// Visual tokens (centralised so polish-tweaks ripple)
// =====================================================================

// Outer container card: subtle emerald-tinted border + soft drop shadow.
// Used by every major widget on the dashboard for consistency.
const PANEL_CLS =
  "relative rounded-2xl border border-emerald-500/10 bg-card " +
  "ring-1 ring-inset ring-emerald-500/5 " +
  "shadow-[0_2px_30px_-12px_rgba(16,185,129,0.18)] " +
  "dark:shadow-[0_2px_40px_-10px_rgba(16,185,129,0.22)]";
// Inner row/card variant (one step less prominent), used inside panels.
const ROW_CLS =
  "rounded-xl border border-emerald-500/10 bg-emerald-500/[0.02] hover:bg-emerald-500/[0.06] " +
  "dark:bg-emerald-500/[0.03] dark:hover:bg-emerald-500/[0.07] transition-colors";

// =====================================================================
// Helpers
// =====================================================================

function norwegianGreeting() {
  const h = new Date().getHours();
  if (h < 6) return "God natt";
  if (h < 11) return "God morgen";
  if (h < 17) return "God dag";
  if (h < 22) return "God kveld";
  return "God natt";
}
function fmtTime(iso) { return iso ? new Date(iso).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" }) : ""; }
function fmtDateShort(iso) {
  return iso ? new Date(iso).toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" }) : "";
}
function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("");
}
function relativeNorwegian(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const now = new Date();
  const diffMs = then - now;
  const absMs = Math.abs(diffMs);
  const mins = Math.round(absMs / 60000);
  const hours = Math.round(mins / 60);
  const days = Math.round(hours / 24);
  let core;
  if (mins < 1) core = "nettopp";
  else if (mins < 60) core = `${mins} min`;
  else if (hours < 24) core = `${hours} t`;
  else core = `${days} d`;
  if (core === "nettopp") return "nå";
  return diffMs >= 0 ? `om ${core}` : `${core} siden`;
}

// Stable per-staff hue palette (kept compatible with Calendar's palette).
const STAFF_HUES = [
  { dot: "bg-emerald-500", bar: "bg-emerald-500/80" },
  { dot: "bg-sky-500",     bar: "bg-sky-500/80" },
  { dot: "bg-amber-500",   bar: "bg-amber-500/80" },
  { dot: "bg-violet-500",  bar: "bg-violet-500/80" },
  { dot: "bg-rose-500",    bar: "bg-rose-500/80" },
  { dot: "bg-cyan-500",    bar: "bg-cyan-500/80" },
];
function staffHue(id) {
  if (!id) return STAFF_HUES[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  return STAFF_HUES[Math.abs(h) % STAFF_HUES.length];
}

// =====================================================================
// Sparkline (unchanged from Phase B)
// =====================================================================

function Sparkline({ values, accent = "#10b981" }) {
  const W = 72;
  const H = 24;
  if (!Array.isArray(values) || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const stepX = W / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * stepX;
    const y = H - ((v - min) / span) * (H - 2) - 1;
    return [x, y];
  });
  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${W},${H} L0,${H} Z`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
      <defs>
        <linearGradient id={`grad-${accent.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.45" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#grad-${accent.replace("#", "")})`} />
      <path d={linePath} fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.5" fill={accent} stroke="white" strokeWidth="0.5" />
    </svg>
  );
}

// =====================================================================
// Avatar — initials with subtle emerald background. Photo support
// gated on `imageUrl` (none in DB today; ready for when we add it).
// =====================================================================

function Avatar({ name, size = "md", testid, imageUrl, hueId }) {
  const sz = size === "sm" ? "w-8 h-8 text-[11px]"
    : size === "lg" ? "w-12 h-12 text-base"
    : "w-10 h-10 text-sm";
  // For non-Calendar contexts (where colour disambiguation matters less),
  // we lean on a soft emerald tint so the dashboard reads as a coherent
  // brand surface. The Calendar page keeps its rainbow per-staff palette.
  return (
    <div
      className={`shrink-0 ${sz} rounded-full overflow-hidden ring-1 ring-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-semibold flex items-center justify-center`}
      data-testid={testid}
      title={name || ""}
    >
      {imageUrl
        ? <img src={imageUrl} alt={name || ""} className="w-full h-full object-cover" />
        : <span>{initials(name)}</span>}
    </div>
  );
}

// =====================================================================
// StatusPill
// =====================================================================

function StatusPill({ status, compact = false }) {
  const map = {
    booked:    { label: "Bekreftet",   cls: "bg-sky-500/15 text-sky-600 dark:text-sky-300 ring-sky-500/30",       dot: "bg-sky-500" },
    pending:   { label: "Venter svar", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30", dot: "bg-amber-500" },
    completed: { label: "Fullført",    cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30", dot: "bg-emerald-500" },
    no_show:   { label: "Ikke møtt",   cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/30",     dot: "bg-rose-500" },
    cancelled: { label: "Avlyst",      cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30", dot: "bg-slate-500" },
  };
  const m = map[status] || map.booked;
  // Compact variant: tighter padding + smaller text so multiple inline
  // elements (time · avatar · name · service · pill) all fit on one row
  // inside the narrow Dashboard "Kommende avtaler" cards.
  const padCls = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span className={`inline-flex items-center gap-1.5 ${padCls} rounded-full font-semibold ring-1 ring-inset ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

// =====================================================================
// KPI card — emerald glow + bigger numbers + inline sparkline
// =====================================================================

function KpiCard({ icon: Icon, label, value, trend, hint, accent = "emerald", series, sparkColour, testid }) {
  const accentMap = {
    emerald: { bg: "from-emerald-500/20 via-emerald-500/0 to-transparent", text: "text-emerald-500", chip: "bg-emerald-500/15" },
    primary: { bg: "from-primary/20 via-primary/0 to-transparent",           text: "text-primary",       chip: "bg-primary/15" },
    amber:   { bg: "from-amber-500/20 via-amber-500/0 to-transparent",       text: "text-amber-500",     chip: "bg-amber-500/15" },
    rose:    { bg: "from-rose-500/20 via-rose-500/0 to-transparent",         text: "text-rose-500",      chip: "bg-rose-500/15" },
  };
  const tone = accentMap[accent] || accentMap.emerald;
  const trendCls =
    trend?.dir === "up" ? "text-emerald-500"
    : trend?.dir === "down" ? "text-rose-500"
    : "text-muted-foreground";
  const TrendIcon =
    trend?.dir === "up" ? ArrowUpRight
    : trend?.dir === "down" ? ArrowDownRight
    : null;

  // Horizontal premium KPI per the May 2026 mockup.
  //   Row 1 → label (left, full row) · icon chip (right)
  //   Row 2 → big number (left, flex-1) · sparkline (right, dedicated area)
  //   Row 3 → trend / hint (left, full row)
  // The sparkline lives inside Row 2 as a flex sibling (NOT absolute),
  // so it always has its own clean horizontal area and never collides
  // with the trend text below it.
  return (
    <Card className={`${PANEL_CLS} overflow-hidden card-hover hover:ring-emerald-500/20 transition-all h-full`} data-testid={testid}>
      {/* Decorative glow — kept fully inside the card on every viewport.
          Previously used `-top-12 -right-12 w-40 h-40` which extended the
          element's bounding rect ~35-50px past the card AND past the
          phone's viewport on iPhone 12 Pro Max (vw=428, glow.right=463).
          Even with overflow-hidden on the card, iOS Safari composites
          filter:blur layers in a way that occasionally leaks past the
          clip rect, manifesting as "dashboard is wider than the screen"
          on real devices. Re-anchoring the glow to `top-0 right-0 w-32
          h-32` keeps it visually identical (still blurred + tucked into
          the top-right corner) without ever overflowing the card. */}
      <div className={`pointer-events-none absolute top-0 right-0 w-32 h-32 rounded-full bg-gradient-to-br ${tone.bg} blur-3xl opacity-90`} />
      <CardContent className="relative px-5 py-5 flex flex-col h-full">
        {/* Row 1 — label + icon chip */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground truncate">
            {label}
          </span>
          <div className={`w-7 h-7 rounded-lg ${tone.chip} ${tone.text} ring-1 ring-emerald-500/20 flex items-center justify-center shrink-0`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
          </div>
        </div>
        {/* Row 2 — number (left, flex-1) + sparkline (right, own space) */}
        <div className="mt-3.5 flex items-end gap-2.5">
          <p className="font-heading text-lg xl:text-xl 2xl:text-xl font-bold tracking-tight tabular-nums leading-none flex-1 min-w-0 truncate">
            {value}
          </p>
          {series && series.length > 0 && (
            <div
              className="hidden lg:block shrink-0 -mb-1"
              data-testid={`${testid}-sparkline`}
            >
              <SparklineLarge values={series} accent={sparkColour} />
            </div>
          )}
        </div>
        {/* Row 3 — trend / hint (full width, never overlaps chart) */}
        <div className="mt-3 flex items-center gap-1.5 text-[11px] sm:text-xs">
          {trend && TrendIcon && (
            <span className={`inline-flex items-center gap-0.5 font-semibold ${trendCls} whitespace-nowrap`}>
              <TrendIcon className="w-3.5 h-3.5" strokeWidth={2.25} />
              {trend.text}
            </span>
          )}
          {!trend && hint && <span className="text-muted-foreground truncate">{hint}</span>}
          {trend && hint && <span className="text-muted-foreground truncate hidden 2xl:inline">· {hint}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// Larger sparkline variant used inside the elongated KPI cards.
function SparklineLarge({ values, accent = "#10b981" }) {
  const W = 50;
  const H = 26;
  if (!Array.isArray(values) || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const stepX = W / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => {
    const x = i * stepX;
    const y = H - ((v - min) / span) * (H - 4) - 2;
    return [x, y];
  });
  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${W},${H} L0,${H} Z`;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
      <defs>
        <linearGradient id={`grad-lg-${accent.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.5" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#grad-lg-${accent.replace("#", "")})`} />
      <path d={linePath} fill="none" stroke={accent} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3" fill={accent} stroke="white" strokeWidth="1" />
    </svg>
  );
}

function parseTrendLabel(label) {
  if (!label) return null;
  if (label.startsWith("+")) return { dir: "up", text: label };
  if (/^-\d/.test(label)) return { dir: "down", text: label };
  if (label === "Samme som forrige uke") return { dir: "flat", text: label };
  if (label === "Ny aktivitet denne uken") return { dir: "up", text: label };
  return { dir: "flat", text: label };
}

// =====================================================================
// Employee Filter Card (rich) — popover with menu for the filter
// =====================================================================

function EmployeeFilterCard({ team, value, onChange, currentUserId, testid }) {
  // Build menu items first so we can both render the popover AND compute
  // the label that goes in the closed-state trigger.
  const items = useMemo(() => {
    if (!team || team.length === 0) return [];
    return [
      { value: "all", label: "Alle ansatte", subtitle: `${team.length} ansatte` },
      { value: "me",  label: "Meg",          subtitle: "kun mine bookinger" },
      ...team
        .filter((t) => t.id !== currentUserId)
        .map((t) => ({ value: t.id, label: t.name || t.email || "Ansatt", subtitle: t.role === "owner" ? "Leder" : "Ansatt" })),
    ];
  }, [team, currentUserId]);

  const [open, setOpen] = useState(false);
  if (!team || team.length === 0) return null;

  const current = items.find((it) => it.value === (value || "all")) || items[0];

  // Avatar stack on the right side — up to 4 visible + "+N" badge.
  const visible = team.slice(0, 4);
  const overflow = Math.max(0, team.length - visible.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${PANEL_CLS} px-5 py-3.5 flex items-center gap-4 min-w-[300px] sm:min-w-[340px] hover:ring-emerald-500/30 transition-all text-left`}
          data-testid={testid}
        >
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              Vis kalender for
            </p>
            <p className="font-semibold text-base mt-0.5 flex items-center gap-1 truncate">
              {current.label}
              <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
            </p>
          </div>
          <div className="flex -space-x-2 shrink-0">
            {visible.map((t) => (
              <div
                key={t.id}
                className="w-8 h-8 rounded-full ring-2 ring-card bg-card overflow-hidden flex items-center justify-center"
                title={t.name}
              >
                {/* No profile photos in DB today; show the ClientFlow mark
                    as the universal fallback in dashboard contexts per spec. */}
                <img
                  src="/clientflow-mark.png"
                  alt={t.name || ""}
                  className="w-full h-full object-cover dark:hidden"
                />
                <img
                  src="/clientflow-mark-dark.png"
                  alt={t.name || ""}
                  className="w-full h-full object-cover hidden dark:block"
                />
              </div>
            ))}
            {overflow > 0 && (
              <div className="w-8 h-8 rounded-full ring-2 ring-card bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 text-[10px] font-bold flex items-center justify-center">
                +{overflow}
              </div>
            )}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-1.5 rounded-2xl border border-emerald-500/20 bg-card/95 backdrop-blur shadow-[0_8px_40px_-8px_rgba(16,185,129,0.25)]"
        align="end"
      >
        <ul className="space-y-0.5">
          {items.map((it) => {
            const active = (value || "all") === it.value;
            return (
              <li key={it.value}>
                <button
                  type="button"
                  onClick={() => { onChange(it.value === "all" ? null : it.value); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors
                    ${active ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "hover:bg-accent/60"}`}
                  data-testid={`employee-option-${it.value}`}
                >
                  <div className="w-9 h-9 rounded-full ring-1 ring-emerald-500/20 overflow-hidden bg-card flex items-center justify-center shrink-0">
                    <img src="/clientflow-mark.png" alt="" className="w-full h-full object-cover dark:hidden" />
                    <img src="/clientflow-mark-dark.png" alt="" className="w-full h-full object-cover hidden dark:block" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{it.label}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{it.subtitle}</p>
                  </div>
                  {active && <Check className="w-4 h-4 text-emerald-500" strokeWidth={2.25} />}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

// =====================================================================
// Topbar — page-internal action bar (Ny booking + Support + Profile)
// =====================================================================

function Topbar({ user, onLogout, onSupport, activityUnreadCount }) {
  // No notifications collection in the DB today, so the "bell" doubles as a
  // jump-to-activity-feed action and shows the same count as the live feed
  // (events in the last 24h). Real data, no placeholder.
  return (
    <div className="flex items-center justify-end gap-2 sm:gap-2.5 flex-wrap">
      <Link to="/kalender?new=1" data-testid="topbar-new-booking">
        <Button className="rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-[0_8px_30px_-10px_rgba(16,185,129,0.6)] gap-1.5 h-10 px-3 sm:px-4">
          <Plus className="w-4 h-4" strokeWidth={2.25} />
          Ny booking
        </Button>
      </Link>
      <button
        type="button"
        onClick={onSupport}
        className="w-10 h-10 rounded-2xl border border-border bg-card hover:bg-accent/60 flex items-center justify-center transition-colors"
        data-testid="topbar-support"
        title="Trenger du hjelp?"
      >
        <LifeBuoy className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
      </button>
      <button
        type="button"
        onClick={() => {
          // Scroll the activity card into view as a "show me what just
          // happened" affordance. Uses the data-testid the card already
          // exposes — no new state needed.
          const el = document.querySelector('[data-testid="activity-card"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
        className="w-10 h-10 relative rounded-2xl border border-border bg-card hover:bg-accent/60 flex items-center justify-center transition-colors"
        data-testid="topbar-activity"
        title="Siste aktivitet"
      >
        <Bell className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
      </button>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-10 pl-1.5 pr-3 rounded-2xl border border-border bg-card hover:bg-accent/60 flex items-center gap-2 transition-colors"
            data-testid="topbar-profile"
          >
            <div className="w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-bold text-xs flex items-center justify-center ring-1 ring-emerald-500/20">
              {initials(user?.name || user?.email || "?")}
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.75} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-56 p-1.5 rounded-2xl border border-emerald-500/20 bg-card/95 backdrop-blur shadow-[0_8px_40px_-8px_rgba(16,185,129,0.25)]"
          align="end"
        >
          <div className="px-3 py-2 border-b border-border/60 mb-1">
            <p className="font-semibold text-sm truncate">{user?.name || "Bruker"}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Link
            to="/innstillinger"
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-accent/60 text-sm"
            data-testid="topbar-profile-settings"
          >
            <Settings className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
            Innstillinger
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-accent/60 text-sm text-left"
            data-testid="topbar-profile-logout"
          >
            <LogOut className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
            Logg ut
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// =====================================================================
// Kommende avtaler
// =====================================================================

function UpcomingAppointments({ items, staffParam }) {
  const shown = items.slice(0, 5);
  const calLink = staffParam ? `/kalender?staff=${encodeURIComponent(staffParam)}` : "/kalender";
  return (
    <Card className={`${PANEL_CLS} h-full`} data-testid="upcoming-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
            <h3 className="font-heading text-lg font-semibold">Kommende avtaler</h3>
          </div>
          <Link
            to={calLink}
            className="text-xs font-semibold text-emerald-500 hover:underline inline-flex items-center gap-1"
            data-testid="upcoming-see-all"
          >
            Se alle <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {shown.length === 0 ? (
          <EmptyState icon={CalendarDays} text="Ingen bookinger de neste 48 timene." hint="Nye bookinger vises automatisk her." />
        ) : (
          <ul className="space-y-2">
            {shown.map((b) => (
              <li
                key={b.id}
                className={`${ROW_CLS} py-2.5 pl-3.5 pr-3 flex items-center gap-3`}
                data-testid="upcoming-item"
              >
                {/* Time + day — fixed left col, compact stacked layout. */}
                <div className="text-left min-w-[46px] leading-tight">
                  <p className="text-[15px] font-bold tabular-nums">{fmtTime(b.start_at)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                    {fmtDateShort(b.start_at).split(" ")[0]}.
                  </p>
                </div>
                <Avatar name={b.customer_name} size="md" testid={`upcoming-avatar-${b.id}`} />
                {/* Name + service on ONE horizontal line. Name on the left
                    (natural width), service muted and pushed to the right
                    via ml-auto so the visual hierarchy reads identical to
                    the mockup. Both truncate gracefully on narrow widths. */}
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  <span className="font-semibold text-sm truncate" data-testid={`upcoming-name-${b.id}`}>
                    {b.customer_name || "Ukjent kunde"}
                  </span>
                  <span
                    className="hidden sm:inline text-[13px] text-muted-foreground truncate ml-auto"
                    data-testid={`upcoming-service-${b.id}`}
                  >
                    {b.service_name || "Tjeneste"}
                  </span>
                </div>
                <StatusPill status={b.status || "booked"} compact />
              </li>
            ))}
          </ul>
        )}
        <div className="mt-5 pt-4 border-t border-border/40">
          <Link
            to={calLink}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-500 hover:underline"
            data-testid="upcoming-full-calendar"
          >
            <CalendarDays className="w-3.5 h-3.5" />
            Se full kalender <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Calendar widget (right column, runs tall)
// =====================================================================

function CalendarWidget({ items, snapshot, team, staffParam, currentStaffLabel }) {
  // Day-focused view: prefer today, but if today has no bookings and
  // there ARE bookings later in the 48h window, show the next day with
  // bookings instead. This guarantees the widget never reads "Rolig
  // dag" when the salon clearly has something on the calendar — and
  // makes the widget useful even when Leder visits the dashboard the
  // evening before a busy day.
  const today = new Date();
  const todayStr = today.toDateString();
  const sortedItems = useMemo(
    () => [...(items || [])].sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
    [items],
  );
  const todaysBookings = sortedItems.filter((b) => new Date(b.start_at).toDateString() === todayStr);
  // Pick display date: today if there are bookings, else the next day
  // with bookings, else today (so the "Rolig dag" empty state still
  // accurately reflects today).
  const displayDate = useMemo(() => {
    if (todaysBookings.length > 0) return today;
    const futureBooking = sortedItems.find((b) => new Date(b.start_at) > today);
    return futureBooking ? new Date(futureBooking.start_at) : today;
  }, [sortedItems, todaysBookings.length, today]);
  const displayDateStr = displayDate.toDateString();
  const shown = sortedItems.filter((b) => new Date(b.start_at).toDateString() === displayDateStr);
  const isToday = displayDateStr === todayStr;

  const calLink = staffParam ? `/kalender?staff=${encodeURIComponent(staffParam)}` : "/kalender";
  // Pull a stable colour per staff_id so the vertical bar next to each
  // booking matches the colour used in the EmployeeFilter avatar stack
  // (and the Calendar page Team view).
  const teamById = useMemo(() => Object.fromEntries((team || []).map((t) => [t.id, t])), [team]);

  return (
    <Card className={`${PANEL_CLS}`} data-testid="mini-calendar-card">
      <CardContent className="p-6">
        {/* Header — title + chevron-down (decorative; cue that this is the
            Calendar widget). Matches the mockup's collapsible look. */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
            Kalender
            <ChevronDown className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          </h3>
        </div>

        {/* Date row + filter scope echo (matches mockup) */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold capitalize">
              {displayDate.toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })}
            </p>
            {!isToday && (
              <Badge variant="outline" className="rounded-full text-[10px] font-semibold border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 h-5 px-1.5">
                Neste dag
              </Badge>
            )}
          </div>
          <span className="text-[11px] text-muted-foreground font-semibold inline-flex items-center gap-1 truncate">
            {currentStaffLabel || "Alle ansatte"}
          </span>
        </div>

        {shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-emerald-500/15 bg-emerald-500/[0.03] p-6 text-center">
            <Coffee className="w-7 h-7 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-sm font-semibold">Rolig dag</p>
            <p className="text-xs text-muted-foreground mt-0.5">Ingen bookinger i dag.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {shown.map((b) => {
              const hue = staffHue(b.staff_id);
              const staffName = teamById[b.staff_id]?.name;
              return (
                <li
                  key={b.id}
                  className={`${ROW_CLS} pl-2 pr-3 py-2.5 flex items-center gap-3 relative`}
                  data-testid={`mini-calendar-item-${b.id}`}
                >
                  <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r ${hue.bar}`} />
                  <span className="text-xs font-bold tabular-nums w-11 shrink-0 ml-1">{fmtTime(b.start_at)}</span>
                  <Avatar name={b.customer_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{b.customer_name || "—"}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {b.service_name}{staffName ? ` · ${staffName.split(" ")[0]}` : ""}
                    </p>
                  </div>
                  <StatusPill status={b.status || "booked"} />
                </li>
              );
            })}
          </ul>
        )}

        {/* CTA button — matches the mockup's primary "Åpne full kalender". */}
        <Link to={calLink} className="block mt-5" data-testid="mini-calendar-cta">
          <Button className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-1.5">
            Åpne full kalender <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Dagens status — bottom-right widget
// =====================================================================

function DagensStatus({ snapshot, items, activity }) {
  // All three stats come from already-fetched data; no extra requests.
  const bookingsToday = snapshot?.bookings_today ?? items.filter(b => new Date(b.start_at).toDateString() === new Date().toDateString()).length;
  const now = new Date();
  const activeNow = items.filter((b) => {
    const s = new Date(b.start_at); const e = new Date(b.end_at);
    return s <= now && now <= e && b.status !== "cancelled";
  }).length;
  // Reminders/confirmation/thank-you mails sent in the last 24h, surfaced
  // so Leder can see automation actually firing.
  const dayAgo = new Date(now.getTime() - 24 * 3600 * 1000);
  const automationCount = (activity || []).filter((e) =>
    ["reminder_email_sent", "confirmation_email_sent", "thank_you_email_sent"].includes(e.type)
    && new Date(e.at) >= dayAgo
  ).length;

  const tiles = [
    { icon: CalendarDays, label: "Bookinger", value: bookingsToday, accent: "text-emerald-500 bg-emerald-500/10" },
    { icon: Users,        label: "Aktive nå", value: activeNow,     accent: "text-sky-500 bg-sky-500/10" },
    { icon: BellRing,     label: "E-poster (24t)", value: automationCount, accent: "text-amber-500 bg-amber-500/10" },
  ];

  return (
    <Card className={`${PANEL_CLS}`} data-testid="status-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
          <h3 className="font-heading text-lg font-semibold">Dagens status</h3>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.label} className={`${ROW_CLS} p-3 text-center`}>
                <div className={`w-8 h-8 rounded-xl ${t.accent} mx-auto flex items-center justify-center mb-2`}>
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <p className="text-xl font-bold tabular-nums">{t.value}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">
                  {t.label}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Recent Activity
// =====================================================================

const ACTIVITY_META = {
  booking_created:         { icon: CalendarPlus, tone: "text-sky-500 bg-sky-500/10",         sent: false },
  booking_cancelled:       { icon: X,            tone: "text-rose-500 bg-rose-500/10",       sent: false },
  booking_completed:       { icon: CheckCircle2, tone: "text-emerald-500 bg-emerald-500/10", sent: false },
  confirmation_email_sent: { icon: Mail,         tone: "text-sky-500 bg-sky-500/10",         sent: true },
  reminder_email_sent:     { icon: BellRing,     tone: "text-amber-500 bg-amber-500/10",     sent: true },
  thank_you_email_sent:    { icon: Heart,        tone: "text-rose-500 bg-rose-500/10",       sent: true },
  customer_created:        { icon: UserPlus,     tone: "text-violet-500 bg-violet-500/10",   sent: false },
};

function RecentActivity({ events }) {
  return (
    <Card className={`${PANEL_CLS} h-full`} data-testid="activity-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
            <h3 className="font-heading text-lg font-semibold">Siste aktivitet</h3>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Live</span>
        </div>
        {(!events || events.length === 0) ? (
          <EmptyState icon={Activity} text="Ingen aktivitet siste 14 dager." hint="Bookinger, e-poster og kanselleringer dukker opp her." />
        ) : (
          <ul className="space-y-3.5">
            {events.slice(0, 6).map((e) => {
              const meta = ACTIVITY_META[e.type] || ACTIVITY_META.booking_created;
              const Icon = meta.icon;
              return (
                <li key={e.id} className="flex items-start gap-3" data-testid="activity-item">
                  <div className={`w-9 h-9 rounded-xl ${meta.tone} ring-1 ring-emerald-500/15 flex items-center justify-center shrink-0`}>
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold leading-tight truncate">{e.title}</p>
                      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                        {relativeNorwegian(e.at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <p className="text-[11px] text-muted-foreground truncate flex-1">{e.subtitle}</p>
                      {meta.sent && <Check className="w-3 h-3 text-emerald-500 shrink-0" strokeWidth={2.5} />}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// AI suggestions
// =====================================================================

function AiSuggestions({ snapshot, reminders, plan }) {
  const suggestions = useMemo(() => {
    const out = [];
    const followCount = snapshot?.follow_up_count || 0;
    if (followCount > 0) {
      out.push({
        id: "follow-up", icon: UserCheck, tone: "text-emerald-500 bg-emerald-500/10",
        title: `${followCount} ${followCount === 1 ? "kunde" : "kunder"} bør følges opp`,
        body: `Marker som fulgt opp eller send en kort melding.`,
        action: { label: "Se liste", to: "/kunder?filter=follow-up" },
      });
    }
    if (snapshot?.quiet_day) {
      const day = snapshot.quiet_day.charAt(0).toUpperCase() + snapshot.quiet_day.slice(1);
      out.push({
        id: "quiet-day", icon: Coffee, tone: "text-amber-500 bg-amber-500/10",
        title: `${day} er rolig denne uka`,
        body: `Åpne ekstra tider eller send et tilbud.`,
        action: { label: "Åpne kalender", to: "/kalender" },
      });
    }
    const upcomingWithoutEmail = (reminders.upcoming_48h || []).filter((b) => !b.customer_email).length;
    if (upcomingWithoutEmail > 0) {
      out.push({
        id: "missing-email", icon: Mail, tone: "text-sky-500 bg-sky-500/10",
        title: `${upcomingWithoutEmail} ${upcomingWithoutEmail === 1 ? "kunde mangler" : "kunder mangler"} e-post`,
        body: `Spør om e-post ved neste besøk for automatisk påminnelse.`,
        action: { label: "Se kunder", to: "/kunder" },
      });
    }
    const inactive = (reminders.inactive_customers || []).length;
    if (out.length < 3 && inactive > 0 && plan !== "demo") {
      out.push({
        id: "inactive", icon: Sparkles, tone: "text-violet-500 bg-violet-500/10",
        title: `${inactive} ${inactive === 1 ? "inaktiv kunde" : "inaktive kunder"} kan vekkes`,
        body: `Send en kort hilsen til kunder som ikke har vært innom på 90+ dager.`,
        action: { label: "Skriv melding", to: "/ai-meldinger" },
      });
    }
    if (out.length === 0) {
      out.push({
        id: "all-good", icon: CheckCircle2, tone: "text-emerald-500 bg-emerald-500/10",
        title: "Alt under kontroll",
        body: "Ingen åpne oppfølginger og kalenderen ser sunn ut.",
        action: null,
      });
    }
    return out.slice(0, 3);
  }, [snapshot, reminders, plan]);

  return (
    <Card className={`${PANEL_CLS} h-full`} data-testid="ai-suggestions-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
            <h3 className="font-heading text-lg font-semibold">AI-forslag</h3>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Smart</span>
        </div>
        <ul className="space-y-3">
          {suggestions.map((s) => {
            const Icon = s.icon;
            return (
              <li key={s.id} className={`${ROW_CLS} p-3.5 flex items-start gap-3`} data-testid={`suggestion-${s.id}`}>
                <div className={`w-9 h-9 rounded-xl ${s.tone} ring-1 ring-emerald-500/15 flex items-center justify-center shrink-0`}>
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm leading-tight">{s.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug">{s.body}</p>
                  {s.action && (
                    <Link
                      to={s.action.to}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-500 hover:underline"
                      data-testid={`suggestion-action-${s.id}`}
                    >
                      {s.action.label} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Quick Actions
// =====================================================================

function QuickActions({ isOwner, bizSlug }) {
  const navigate = useNavigate();
  const [allOpen, setAllOpen] = useState(false);
  // Mockup-aligned action set: 6 tiles in a 2×3 grid + "Se alle handlinger"
  // CTA below. We keep every route functional (no dead links).
  const actions = [
    { id: "new-booking",          icon: CalendarPlus,  label: "Ny booking",        to: "/kalender?new=1" },
    { id: "new-customer",         icon: UserPlus,      label: "Ny kunde",          to: "/kunder?new=1" },
    { id: "new-customer-booking", icon: CalendarCheck, label: "Ny kunde + booking", to: "/kunder?new=1&then_book=1" },
    { id: "send-message",         icon: MessageCircle, label: "Send melding",      to: "/ai-meldinger" },
    { id: "campaign",             icon: Megaphone,     label: "Opprett kampanje",  to: "/ai-meldinger?type=campaign" },
    ...(isOwner
      ? [{ id: "reports",   icon: BarChart3, label: "Se rapporter", to: "/rapporter" }]
      : [{ id: "customers", icon: Users,     label: "Alle kunder",  to: "/kunder" }]
    ),
  ];
  return (
    <Card className={`${PANEL_CLS} h-full`} data-testid="quick-actions-card">
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex items-center gap-2 mb-5">
          <Zap className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
          <h3 className="font-heading text-lg font-semibold">Hurtighandlinger</h3>
        </div>
        <div className="grid grid-cols-2 auto-rows-fr gap-3 flex-1">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => navigate(a.to)}
                className={`${ROW_CLS} px-2 py-5 text-center flex flex-col items-center justify-center gap-2.5 hover:ring-1 hover:ring-emerald-500/30 hover:-translate-y-0.5 transition-all duration-200`}
                data-testid={`quick-action-${a.id}`}
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-500 ring-1 ring-emerald-500/15 flex items-center justify-center shadow-[0_0_0_1px_rgba(16,185,129,0.08)]">
                  <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                </div>
                <span className="font-semibold text-[13px] leading-tight text-center">{a.label}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setAllOpen(true)}
          className="mt-4 w-full rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.09] hover:border-emerald-500/25 transition-all py-2.5 text-sm font-semibold text-emerald-500 inline-flex items-center justify-center gap-1.5"
          data-testid="quick-action-see-all"
        >
          Se alle handlinger <ArrowRight className="w-4 h-4" />
        </button>
      </CardContent>
      <AllActionsDialog
        open={allOpen} onOpenChange={setAllOpen}
        isOwner={isOwner} bizSlug={bizSlug}
      />
    </Card>
  );
}

// =====================================================================
// All Actions Modal — opened by "Se alle handlinger"
// =====================================================================
// Surfaces every working action in the product, grouped by area. Only
// uses existing routes — no new backend, no filler. Role gating is
// hard: owner-only items don't render at all for staff so they can't
// land on a redirect page.
// =====================================================================

function AllActionsDialog({ open, onOpenChange, isOwner, bizSlug }) {
  const navigate = useNavigate();
  const go = (to) => { onOpenChange(false); navigate(to); };

  const copyBookingLink = async () => {
    if (!bizSlug) {
      toast.error("Bookinglenke er ikke tilgjengelig ennå.");
      return;
    }
    const origin = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, "")
      || (typeof window !== "undefined" ? window.location.origin : "");
    const url = `${origin}/book/${bizSlug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Bookinglenke kopiert");
    } catch (_) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Bookinglenke kopiert");
      } catch (_) {
        toast.error("Kunne ikke kopiere bookinglenke");
      }
    }
    onOpenChange(false);
  };

  const groups = [
    {
      title: "Bookinger & kunder",
      items: [
        { id: "open-calendar", icon: CalendarDays, label: "Åpne kalender", onClick: () => go("/kalender") },
        { id: "new-booking", icon: CalendarPlus, label: "Ny booking", onClick: () => go("/kalender?new=1") },
        { id: "new-customer", icon: UserPlus, label: "Ny kunde", onClick: () => go("/kunder?new=1") },
        { id: "new-customer-booking", icon: CalendarCheck, label: "Ny kunde + booking", onClick: () => go("/kunder?new=1&then_book=1") },
        { id: "follow-up", icon: UserCheck, label: "Kunder å følge opp", onClick: () => go("/kunder?filter=follow-up") },
      ],
    },
    {
      title: "Markedsføring",
      items: [
        { id: "send-message", icon: MessageCircle, label: "Send AI-melding", onClick: () => go("/ai-meldinger") },
        { id: "campaign", icon: Megaphone, label: "Opprett kampanje", onClick: () => go("/ai-meldinger?type=campaign") },
      ],
    },
    // Owner-only group — completely hidden for staff so they don't land
    // on a redirect or 403 page.
    ...(isOwner
      ? [{
          title: "Drift",
          items: [
            { id: "new-service", icon: Scissors, label: "Opprett tjeneste", onClick: () => go("/tjenester") },
            { id: "invite-staff", icon: UserCog, label: "Inviter ansatt", onClick: () => go("/personal") },
            { id: "copy-link", icon: Link2, label: "Kopier bookinglenke", onClick: copyBookingLink },
            { id: "see-reports", icon: BarChart3, label: "Se rapporter", onClick: () => go("/rapporter") },
          ],
        }]
      : []
    ),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="all-actions-dialog">
        <DialogHeader>
          <DialogTitle>Alle handlinger</DialogTitle>
          <DialogDescription>
            Velg en handling for å hoppe rett dit du trenger.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2.5">
                {g.title}
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {g.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={it.onClick}
                      className="flex items-center gap-3 rounded-xl border border-emerald-500/10 bg-emerald-500/[0.02] hover:bg-emerald-500/[0.08] hover:border-emerald-500/30 hover:-translate-y-0.5 transition-all p-3 text-left"
                      data-testid={`all-action-${it.id}`}
                    >
                      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 text-emerald-500 ring-1 ring-emerald-500/15 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4" strokeWidth={1.75} />
                      </div>
                      <span className="font-semibold text-[13px] leading-tight">{it.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ icon: Icon, text, hint }) {
  return (
    <div className="py-8 text-center">
      <div className="w-10 h-10 rounded-full bg-muted/50 mx-auto flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <p className="text-sm font-medium">{text}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

// =====================================================================
// MyWorkdayCard — compact personal Timelister widget for the dashboard
// =====================================================================
// Surfaces the logged-in user's current workday state with one tap:
//   * Not started → "Start arbeidsdag"
//   * Working     → "Start pause" + "Stopp arbeidsdag"
//   * On break    → "Avslutt pause"
//   * Submitted   → status pill + link to Timelister
//   * Approved    → status pill + link to Timelister
//
// Uses ONLY the existing /api/timelister/me + workday endpoints. No
// duplicate timekeeping logic. Hidden silently when the user's business
// doesn't have Timelister enabled (the endpoint returns 402 for non-
// Premium plans, which we treat as "skip this widget").
// =====================================================================
function MyWorkdayCard() {
  const [data, setData] = useState(null);
  const [weekSummary, setWeekSummary] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  // Tick every 60 seconds so the live running-workday minutes update
  // visibly without polling the API. Only mounted while the widget is.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const refresh = useCallback(async () => {
    try {
      // /timelister/me gives running entry + breaks + status; the
      // week-summary endpoint gives the *closed* worked minutes for the
      // current Monday→Sunday window (handles week-spans-month cleanly).
      const [meRes, weekRes] = await Promise.all([
        api.get("/timelister/me"),
        api.get("/timelister/me/week-summary"),
      ]);
      setData(meRes.data);
      setWeekSummary(weekRes.data);
    } catch (err) {
      // 402 (plan gate) or 403 (no access) → hide the widget entirely.
      // Anything else, also hide silently so the dashboard never breaks.
      setHidden(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (hidden || !data) return null;

  const isDemo = !!data.demo;
  const running = data.running_entry;
  const activeBreak = data.active_break;
  const status = data.timesheet?.status || "draft";

  const fmtTimeOslo = (iso) => iso
    ? new Date(iso).toLocaleTimeString("nb-NO", { timeZone: "Europe/Oslo", hour: "2-digit", minute: "2-digit" })
    : "";

  const startWorkday = async () => {
    if (busy || isDemo) return;
    setBusy(true);
    try {
      await api.post("/timelister/workday/start");
      toast.success("Arbeidsdag startet");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke starte."); }
    finally { setBusy(false); }
  };

  const stopWorkday = async (confirmBreak = false) => {
    if (busy || isDemo) return;
    setBusy(true);
    try {
      await api.post("/timelister/workday/stop", { confirm_close_break: confirmBreak });
      toast.success("Arbeidsdag stoppet");
      await refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "active_break") {
        const ok = window.confirm(
          "Du har en pågående pause. Stopper du arbeidsdagen nå, avsluttes pausen samtidig. Fortsett?"
        );
        if (ok) {
          setBusy(false);
          await stopWorkday(true);
          return;
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Kunne ikke stoppe.");
      }
    } finally { setBusy(false); }
  };

  const startBreak = async () => {
    if (busy || isDemo) return;
    setBusy(true);
    try {
      await api.post("/timelister/break/start");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke starte pause."); }
    finally { setBusy(false); }
  };

  const endBreak = async () => {
    if (busy || isDemo) return;
    setBusy(true);
    try {
      await api.post("/timelister/break/end");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke avslutte pause."); }
    finally { setBusy(false); }
  };

  // Compute state label + tone (drives the small status pill).
  let stateLabel, stateTone, stateHint;
  if (status === "submitted") {
    stateLabel = "Innsendt";
    stateTone = "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30";
    stateHint = "Vent på lederens vurdering.";
  } else if (status === "approved") {
    stateLabel = "Godkjent";
    stateTone = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30";
    stateHint = "Timelisten for perioden er godkjent.";
  } else if (activeBreak) {
    stateLabel = "Pause pågår";
    stateTone = "bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-500/30";
    stateHint = `Pause startet kl. ${fmtTimeOslo(activeBreak.start_at)}`;
  } else if (running) {
    stateLabel = "Pågår";
    stateTone = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30";
    stateHint = `Startet kl. ${fmtTimeOslo(running.start_at)}`;
  } else {
    stateLabel = "Ikke startet";
    stateTone = "bg-muted text-muted-foreground ring-border";
    stateHint = "Trykk «Start arbeidsdag» for å begynne.";
  }

  // Submitted / approved → compact read-only state + jump link.
  const readOnly = status === "submitted" || status === "approved";

  // ---- Weekly total (Mon–Sun, Europe/Oslo) ----
  // Closed minutes come from the server (so weeks crossing a calendar
  // month are handled correctly). Live minutes come from the running
  // entry (if any) and are reduced by the current/active break the
  // exact same way the server's _worked_minutes does — i.e. subtract
  // every unpaid break that overlaps the running entry, counting an
  // active break up to "now".
  const liveRunningMin = (() => {
    if (!running || !running.start_at || !weekSummary) return 0;
    // Only count the running entry if its Oslo calendar date is within
    // the current Mon–Sun window. Compare YYYY-MM-DD strings — robust
    // across CET/CEST DST transitions, no UTC-offset arithmetic needed.
    const startOsloDate = new Date(running.start_at).toLocaleDateString("sv-SE", { timeZone: "Europe/Oslo" });
    if (startOsloDate < weekSummary.week_start) return 0;
    const startMs = new Date(running.start_at).getTime();
    const totalMin = Math.max(0, Math.floor((nowTick - startMs) / 60000));
    let breakMin = 0;
    for (const b of (data?.breaks || [])) {
      if (b.time_entry_id !== running.id) continue;
      if (b.paid) continue;
      const bs = b.start_at ? new Date(b.start_at).getTime() : null;
      if (!bs) continue;
      const be = b.end_at ? new Date(b.end_at).getTime() : nowTick;
      breakMin += Math.max(0, Math.floor((Math.min(be, nowTick) - bs) / 60000));
    }
    return Math.max(0, totalMin - breakMin);
  })();
  const weekTotalMin = (weekSummary?.closed_worked_min || 0) + liveRunningMin;
  const weekHours = Math.floor(weekTotalMin / 60);
  const weekMins = weekTotalMin % 60;
  const weekText = weekHours > 0
    ? `${weekHours} t ${String(weekMins).padStart(2, "0")} min`
    : `${weekMins} min`;

  return (
    <Card className={`${PANEL_CLS}`} data-testid="my-workday-card">
      <CardContent className="p-4 sm:p-5">
        {/* Mobile: text block on top, action buttons on a new row below.
            Desktop (sm+): everything on one row with buttons aligned right. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 ring-1 ring-emerald-500/20 flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-500">
                  Min arbeidsdag
                </p>
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ring-1 ring-inset text-[10px] font-semibold ${stateTone}`}
                  data-testid="my-workday-status"
                >
                  {stateLabel}
                </span>
              </div>
              <p className="text-sm font-semibold mt-0.5" data-testid="my-workday-hint">
                {stateHint}
              </p>
            {weekSummary && (
              <>
                <p
                  className="text-xs text-muted-foreground mt-0.5 tabular-nums"
                  data-testid="my-workday-week-total"
                  title="Registrert mandag–søndag (Europe/Oslo). Pauser og slettede oppføringer er ekskludert."
                >
                  Registrert denne uken: <span className="font-medium text-foreground">{weekText}</span>
                </p>
                {/* Explicit visible link — the weekly total above stays
                    informational (not a hidden click target). Navigates
                    to Timelister on "Min tid" (default tab) so the user
                    sees their current week's entries in detail. */}
                <Link
                  to="/timelister"
                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:underline mt-1"
                  data-testid="my-workday-see-timesheet"
                >
                  Se timeliste <ArrowRight className="w-3 h-3" strokeWidth={2.25} />
                </Link>
              </>
            )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3 sm:mt-0 sm:ml-auto sm:shrink-0">
            {readOnly ? (
              <Link to="/timelister" data-testid="my-workday-open">
                <Button variant="outline" className="rounded-xl h-9 px-3.5 text-sm font-semibold">
                  Åpne Timelister <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            ) : !running ? (
              <Button
                onClick={startWorkday}
                disabled={busy || isDemo}
                className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-9 px-3.5 text-sm gap-1.5"
                data-testid="my-workday-start"
              >
                <Play className="w-4 h-4" strokeWidth={2} /> Start arbeidsdag
              </Button>
            ) : activeBreak ? (
              <Button
                onClick={endBreak}
                disabled={busy || isDemo}
                className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-9 px-3.5 text-sm gap-1.5"
                data-testid="my-workday-end-break"
              >
                <Coffee className="w-4 h-4" strokeWidth={2} /> Avslutt pause
              </Button>
            ) : (
              <>
                <Button
                  onClick={startBreak}
                  disabled={busy || isDemo}
                  variant="outline"
                  className="rounded-xl h-9 px-3.5 text-sm font-semibold gap-1.5"
                  data-testid="my-workday-start-break"
                >
                  <Coffee className="w-4 h-4" strokeWidth={2} /> Start pause
                </Button>
                <Button
                  onClick={() => stopWorkday(false)}
                  disabled={busy || isDemo}
                  variant="outline"
                  className="rounded-xl h-9 px-3.5 text-sm font-semibold gap-1.5"
                  data-testid="my-workday-stop"
                >
                  <Square className="w-4 h-4" strokeWidth={2} /> Stopp arbeidsdag
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}



// =====================================================================
// Page
// =====================================================================

export default function Dashboard() {
  const { open: openContact } = useContact();
  const { isPlatformAdmin, user, logout } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";
  const [stats, setStats] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [reminders, setReminders] = useState({ upcoming_48h: [], missed_followups: [], inactive_customers: [] });
  const [kpiSeries, setKpiSeries] = useState(null);
  const [activity, setActivity] = useState([]);
  const [team, setTeam] = useState([]);
  const [staffFilter, setStaffFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bizSlug, setBizSlug] = useState(null);

  const staffQuery = useCallback(() => {
    if (!staffFilter) return {};
    return { staff_id: staffFilter };
  }, [staffFilter]);

  useEffect(() => {
    (async () => {
      try {
        const b = await api.get("/business").catch(() => ({ data: null }));
        if (b.data) setBizSlug(b.data.slug);
      } catch (_) { /* non-critical */ }
      if (isOwner) {
        try {
          const t = await api.get("/staff");
          setTeam(t.data || []);
        } catch (_) { /* non-critical */ }
      }
    })();
  }, [isOwner]);

  // Re-fetch all dashboard data whenever the staff filter changes, OR
  // when the tab regains visibility — the latter so a booking created
  // on the Calendar page (then user navigates back here) refreshes the
  // Kommende avtaler list, Calendar widget, KPI cards and activity feed
  // without requiring a hard reload. The Mongo-driven endpoints are
  // cheap enough to refetch on every visibility-resume.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") setRefreshTick((t) => t + 1);
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = staffQuery();
        const [s, snap, r, k, a] = await Promise.all([
          api.get("/dashboard/stats", { params }),
          api.get("/dashboard/snapshot", { params }).catch(() => ({ data: null })),
          api.get("/reminders", { params }),
          api.get("/dashboard/kpi-series", { params }).catch(() => ({ data: null })),
          api.get("/dashboard/activity", { params }).catch(() => ({ data: { events: [] } })),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setSnapshot(snap.data);
        setReminders(r.data);
        setKpiSeries(k.data);
        setActivity(a.data?.events || []);
      } catch (e) {
        if (!cancelled) toast.error("Kunne ikke laste oversikt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [staffFilter, staffQuery, refreshTick]);

  if (loading || !stats) {
    return (
      <div className="space-y-8 anim-in" data-testid="dashboard-loading">
        <HeaderSkeleton />
        <StatGridSkeleton count={4} />
      </div>
    );
  }

  const planBadge = { demo: "Demo", basic: "Basic", pro: "Pro", premium: "Premium" }[stats.plan] || stats.plan;
  const isDemoPlan = stats.plan === "demo";
  const isDemo = bizSlug === "nordlys-frisor";
  const firstName = (user?.name || "").trim().split(" ")[0] || "";
  const greeting = norwegianGreeting();
  const filterUserName = (() => {
    if (!staffFilter || staffFilter === "all") return null;
    if (staffFilter === "me") return firstName;
    const m = team.find((t) => t.id === staffFilter);
    return m ? (m.name || "").split(" ")[0] : null;
  })();
  const summaryBits = [];
  if (snapshot?.bookings_today != null) {
    const word = snapshot.bookings_today === 1 ? "booking" : "bookinger";
    summaryBits.push(filterUserName
      ? `${filterUserName} har ${snapshot.bookings_today} ${word} i dag`
      : `Salongen har ${snapshot.bookings_today} ${word} i dag`);
  }
  if (!filterUserName && snapshot?.follow_up_enabled && snapshot?.follow_up_count > 0) {
    summaryBits.push(`${snapshot.follow_up_count} ${snapshot.follow_up_count === 1 ? "kunde bør følges opp" : "kunder bør følges opp"}`);
  }
  if (summaryBits.length === 0) summaryBits.push("Kalenderen er rolig i dag");

  const trend = parseTrendLabel(stats.week_over_week_label);
  const staffParam = staffFilter || "";
  // 24h unread count for the topbar bell badge.
  const dayAgo = new Date(Date.now() - 24 * 3600 * 1000);
  const unread24h = (activity || []).filter((e) => new Date(e.at) >= dayAgo).length;

  return (
    <div data-testid="dashboard-page" className="space-y-6 sm:space-y-7 anim-in min-w-0 max-w-full">
      {/* ============ Topbar ============ */}
      <Topbar
        user={user}
        onLogout={logout}
        onSupport={() => openContact({ source: "dashboard-topbar-support", defaultMessage: "Hei! Trenger litt hjelp med ClientFlow." })}
        activityUnreadCount={unread24h}
      />

      {/* ============ Header + Employee Filter row ============ */}
      <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.18em] font-semibold text-emerald-500">Oversikt</p>
            {isDemo && (
              <Badge
                variant="outline"
                className="rounded-full border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800 text-[10px]"
                data-testid="demo-data-badge"
              >
                Demo-data
              </Badge>
            )}
            <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold ml-1" data-testid="plan-badge">
              {planBadge}
            </Badge>
          </div>
          <h1 className="mt-1.5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {greeting}{firstName ? <>, <span className="text-emerald-500">{firstName}</span></> : ""} <span className="inline-block">👋</span>
          </h1>
          <p className="text-base text-muted-foreground mt-2.5 max-w-2xl leading-relaxed">
            {summaryBits.join(" · ")}
            {snapshot?.next_booking && (
              <> · Neste kunde <span className="font-semibold text-foreground">{snapshot.next_booking.customer_name || ""}</span> kl. <span className="font-semibold text-foreground tabular-nums">{fmtTime(snapshot.next_booking.start_at)}</span></>
            )}
            .
          </p>
        </div>
        {isOwner && team.length > 1 && (
          <EmployeeFilterCard
            team={team}
            value={staffFilter}
            onChange={setStaffFilter}
            currentUserId={user?.id}
            testid="employee-filter"
          />
        )}
      </div>

      {isPlatformAdmin && <PricingInterestCard />}

      {/* Personal Timelister quick action — visible for users whose
          business has Timelister enabled (Premium plan or Nordlys demo).
          Uses the existing /timelister/me + workday endpoints, no
          duplicate timekeeping logic. Hidden silently for Basic/Pro
          plans because the endpoint 402s. */}
      <MyWorkdayCard />

      {isDemoPlan && (
        <div className="rounded-2xl border border-amber-300/60 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-800/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="demo-limit-banner">
          <div className="flex-1">
            {isDemo ? (
              <>
                <p className="font-semibold">Demo-data: Bookinggrensen gjelder kun nye demo-bookinger.</p>
                <p className="text-sm text-muted-foreground">
                  Eksisterende bookinger og kunder er testdata. Du kan opprette {Math.max(0, (stats.demo_plan_limit ?? 1) - stats.bookings_this_month)} ny demo-booking denne måneden.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold">Demo-plan: {stats.bookings_this_month} / {stats.demo_plan_limit ?? 1} booking denne måneden</p>
                <p className="text-sm text-muted-foreground">Privat demo. Betaling og oppgradering åpnes etter avtale.</p>
              </>
            )}
          </div>
          <Button
            onClick={() => openContact({ source: "dashboard-demo-banner", defaultMessage: "Vi vil oppgradere fra Demo-planen." })}
            className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="demo-limit-upgrade"
          >
            Be om tilgang
          </Button>
        </div>
      )}

      {/* ============ Main 12-col grid: KPIs+content (8 cols) | Calendar (4 cols) ============ */}
      <div className="grid lg:grid-cols-12 gap-5 lg:gap-6">
        {/* LEFT: KPIs + content stack */}
        <div className="lg:col-span-8 space-y-5 lg:space-y-6">
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            <KpiCard
              icon={TrendingUp} label="Omsetning" value={formatNOK(stats.revenue_week_nok)}
              hint={`${formatNOK(stats.revenue_month_nok)} i mnd`}
              accent="emerald" series={kpiSeries?.series?.revenue} sparkColour="#10b981"
              testid="stat-revenue"
            />
            <KpiCard
              icon={CalendarDays} label="Bookinger" value={stats.bookings_this_week}
              trend={trend} hint={`${stats.bookings_today} i dag`}
              accent="emerald" series={kpiSeries?.series?.bookings} sparkColour="#10b981"
              testid="stat-bookings"
            />
            <KpiCard
              icon={UserCheck} label="Faste kunder" value={stats.repeat_customers}
              hint={`${stats.total_customers} totalt`}
              accent="emerald" series={kpiSeries?.series?.new_customers} sparkColour="#8b5cf6"
              testid="stat-repeat"
            />
            <KpiCard
              icon={AlertTriangle} label="Ikke møtt" value={stats.no_shows_month}
              hint="denne måneden"
              accent={stats.no_shows_month > 0 ? "rose" : "emerald"}
              series={kpiSeries?.series?.no_shows}
              sparkColour={stats.no_shows_month > 0 ? "#f43f5e" : "#94a3b8"}
              testid="stat-noshow"
            />
          </div>

          {/* Kommende avtaler + Kunder å følge opp — equal-height row.
              The taller card defines the height; the shorter one
              stretches to match for visual balance per the mockup.
              Slight 3/2 width split favours the appointments list so
              the horizontal name+service layout has breathing room
              (FollowUpList is most often in its empty state). */}
          {stats.plan !== "basic" ? (
            <div className="grid lg:grid-cols-5 gap-5 lg:gap-6">
              <div className="lg:col-span-3">
                <UpcomingAppointments items={reminders.upcoming_48h || []} staffParam={staffParam} />
              </div>
              <div className="lg:col-span-2">
                <FollowUpList />
              </div>
            </div>
          ) : (
            <UpcomingAppointments items={reminders.upcoming_48h || []} staffParam={staffParam} />
          )}

          {/* Activity + AI + Quick actions — always three columns. */}
          <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
            <RecentActivity events={activity} />
            <AiSuggestions snapshot={snapshot} reminders={reminders} plan={stats.plan} />
            <QuickActions isOwner={isOwner} bizSlug={bizSlug} />
          </div>
        </div>

        {/* RIGHT: Calendar widget + Dagens status (tall column) */}
        <div className="lg:col-span-4 space-y-5 lg:space-y-6">
          <CalendarWidget
            items={reminders.upcoming_48h || []}
            snapshot={snapshot}
            team={team}
            staffParam={staffParam}
            currentStaffLabel={
              !staffFilter || staffFilter === "all"
                ? "Alle ansatte"
                : (staffFilter === "me" ? (firstName || "Meg") : (team.find((t) => t.id === staffFilter)?.name?.split(" ")[0] || "Ansatt"))
            }
          />
          <DagensStatus snapshot={snapshot} items={reminders.upcoming_48h || []} activity={activity} />
        </div>
      </div>
    </div>
  );
}
